import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useReducer } from "react";
import ReactModal from "react-modal";
import { Datepicker, momentTimezone } from "@mobiscroll/react";
import NumberFormat from "react-number-format";
import Select from "react-select";
import moment from "moment-timezone";
import "@mobiscroll/react/dist/css/mobiscroll.min.css";
import { toast } from "react-toastify";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import ai from "../../helpers/axios";
import getApiErrorMessage from "../../helpers/get-api-error-message";
import TextAreaInput from "../textarea-input";
import formatPaymentType, {
  CLIENT_PAYMENT_TYPE_OPTIONS,
  OPERATOR_PAYMENT_TYPE_OPTIONS,
} from "../../helpers/format-payment-type";
import shortenName from "../../helpers/shorten-name";
import { LoadingButton } from "../shared/loading-button";
import PaymentUnlinkModal from "../payments-linking/payment-unlink-modal";
import PaymentLinkModal from "../payments-linking/payment-link-modal";
import {
  formatContractOption,
  formatOFROption,
  formatOperatorOption,
  formatProspectOption,
  formatTripOption,
} from "../payments-linking/helpers";
import Tooltip from "@mui/material/Tooltip";

momentTimezone.moment = moment;

const toastOptions = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

const formatContractOptionLabel = (contract) => `${contract.contractTag} ${contract.legs?.[0]?.aircraft?.name || "N/A"} - ${contract.pricing.flightCharges.toLocaleString("en-US", {
  style: "currency",
  currency: "USD",
})}`;
const formatOFROptionLabel = (ofr) => `${ofr.operator?.name || "N/A"} ${ofr.aircraft?.name || "N/A"} ${ofr.tailNumber || ""} - ${ofr.totalClientPayment.toLocaleString("en-US", {
  style: "currency",
  currency: "USD",
})}`;

const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged": {
      if (action.field === "transactionType" && state.amount) {
        return {
          ...state,
          [action.field]: action.value,
          amount: Math.abs(state.amount) * (action.value?.value === "spent" ? -1 : 1),
        };
      }

      if (action.field === "amount" && state.transactionType?.value) {
        return {
          ...state,
          amount: Math.abs(action.value) * (state.transactionType?.value === "spent" ? -1 : 1),
        };
      }

      return {
        ...state,
        [action.field]: action.value,
      };
    }

    case "dataBeganDownloading": {
      return {
        ...state,
        loading: true,
      };
    }

    case "dataDownloaded": {
      return {
        ...state,
        loading: false,
      };
    }

    case "resetForm": {
      return {
        ...state,

        paymentDate: "",
        paymentType: "",
        transactionType: "",
        amount: "",
        recordBeingEdited: null,
      };
    }

    case "addRecordForm": {
      let selectedContract = null;
      if (state.showContractSelect && state.contracts?.length === 1) {
        const [contract] = state.contracts;
        selectedContract = {
          _id: contract._id,
          label: contract ? formatContractOptionLabel(contract) : "",
          value: contract._id,
        };
      }
      let selectedOFR = null;
      if (state.showOFRSelect && state.operatorFinancialRecords?.length === 1) {
        const [ofr] = state.operatorFinancialRecords;
        selectedOFR = {
          _id: ofr._id,
          label: ofr ? formatOFROptionLabel(ofr) : "",
          value: ofr._id,
          operator: ofr.operator,
        };
      }

      return {
        ...state,
        showFormModal: true,
        modalHeaderText: "Add",
        selectedContract,
        selectedOFR,
      };
    }

    case "editRecordForm": {
      let record = action.record;

      let paymentType = null;
      //handle select fields
      if (record.paymentType) {
        paymentType = {
          label: formatPaymentType(record.paymentType),
          value: record.paymentType,
        };
      }

      const transactionType = record.amount >= 0 ? {
        label: "Received",
        value: "received",
      } : {
        label: "Spent",
        value: "spent",
      };

      let selectedContract = {
        value: null,
        label: "N/A",
      };
      if (record.contractId) {
        const contract = state.contracts?.find(({ _id }) => _id === record.contractId);
        selectedContract = {
          _id: record.contractId,
          label: contract ? formatContractOptionLabel(contract) : "",
          value: record.contractId,
        };
      }
      let selectedOFR = {
        value: null,
        label: "N/A",
      };
      if (record.ofrId) {
        const ofr = state.operatorFinancialRecords?.find(({ _id }) => _id === record.ofrId);
        selectedOFR = {
          _id: record.ofrId,
          label: ofr ? formatOFROptionLabel(ofr) : "",
          value: record.ofrId,
          operator: ofr.operator,
        };
      }

      return {
        ...state,
        showFormModal: true,
        modalHeaderText: "Edit",
        ...record,
        paymentType,
        transactionType,
        selectedContract,
        selectedOFR,
      };
    }

    case "linkModalFieldChanged": {
      return {
        ...state,
        linkModalValues: {
          ...state.linkModalValues,
          [action.field]: action.value,
        },
      };
    }

    default:
      break;
  }

  return state;
};

const PaymentModuleRow = ({
  record,
  columns,
  contracts,
  operatorFinancialRecords,
  handleUnlinkExternalPayment,
  handleEditButton,
  handleDeleteButton,
  onUnlinkLedgerPayment,
  onRevertFundsMovement,
  isAccountingLedger,
  readOnly,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleOpen = useCallback((event) => {
    event.stopPropagation();

    setIsOpen(open => !open);
  }, []);

  const onEdit = useCallback((event) => {
    event.stopPropagation();
    handleEditButton(record);
  }, [handleEditButton, record]);

  const onDelete = useCallback((event) => {
    event.stopPropagation();
    handleDeleteButton(record);
  }, [handleDeleteButton, record]);

  const onUnlinkExternalPayment = useCallback((event) => {
    event.stopPropagation();
    handleUnlinkExternalPayment(record, record.xeroTransactionId ? "operators" : "clients");
  }, [handleUnlinkExternalPayment, record]);

  const onUnlinkLedger = useCallback((event) => {
    event.stopPropagation();
    onUnlinkLedgerPayment && onUnlinkLedgerPayment(record);
  }, [onUnlinkLedgerPayment, record]);

  const onRevertFunds = useCallback((event) => {
    event.stopPropagation();
    onRevertFundsMovement && onRevertFundsMovement(record);
  }, [onRevertFundsMovement, record]);

  return (
    <>
      <tr className="fm-l2-parent-table-row" key={record._id} style={{
        backgroundColor: record.paymentType === "ccAuthorization" ? "#F6D992" : "",
      }}>
        <td>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={toggleOpen}
            className="p-0"
          >
            {isOpen ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
          </IconButton>
        </td>
        {columns.map((column) => {
          let value = record[column.key];

          if (column.key === "paymentDate") {
            value = moment(value).format("M/D/YY");
          }

          if (column.key === "enteredBy") {
            value = shortenName(record?.enteredBy?.name);
          }

          if (column.key === "contractId") {
            value = "N/A";

            if (record.contractId) {
              const contract = contracts?.find(({ _id }) => _id === record.contractId);
              if (contract) {
                value = formatContractOptionLabel(contract);
                value = (
                  <Tooltip
                    classes={{
                      tooltip: "multiline-tooltip",
                    }}
                    title={value}
                  >
                    <div className="two-line-content">
                      {value}
                    </div>
                  </Tooltip>
                );
              }
            }
          }

          if (column.key === "ofrId") {
            value = "N/A";

            if (record.ofrId) {
              const ofr = operatorFinancialRecords?.find(({ _id }) => _id === record.ofrId);
              if (ofr) {
                value = formatOFROptionLabel(ofr);
                value = (
                  <Tooltip
                    classes={{
                      tooltip: "multiline-tooltip",
                    }}
                    title={value}
                  >
                    <div className="two-line-content">
                      {value}
                    </div>
                  </Tooltip>
                );
              }
            }
          }

          if (column.key === "transactionType") {
            value = record?.amount >= 0 ? "Received" : "Spent";
          }

          if (column.key === "paymentType") {
            value = formatPaymentType(value);
          }

          if (column.key === "amount") {
            //currency
            const isPositive = value >= 0;

            value = value
              ? value.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })
              : "";

            return (
              <td
                key={`column-${column.label}`}
                className={`text-align-right amount-${isPositive ? "positive" : "negative"}`}
              >
                {value}
              </td>
            );
          }

          return (
            <td
              key={`column-${column.label}`}
            >
              {value}
            </td>
          );
        })}
        <td>
          <div className="d-flex justify-content-end">
            {readOnly ? null : (
              <>
            {record.transferGroupId || record.plaidTransactionId || record.xeroTransactionId || record.fundsLedgerId ? null : (
              <button
                className="base-button icon-button icon-button-small evo-blue"
                onClick={onEdit}
                title="Edit"
              >
                <span className="material-icons">edit</span>
              </button>
            )}

            {record.transferGroupId ? (
              isAccountingLedger ? (
                <button
                  onClick={onRevertFunds}
                  className="d-block base-button icon-button icon-button-small btn-danger ml-2"
                  title="Revert Funds Movement"
                >
                  <span className="material-icons">undo</span>
                </button>
              ) : null
            ) : (record.plaidTransactionId || record.xeroTransactionId) ? (
              <button
                onClick={onUnlinkExternalPayment}
                className="d-block base-button icon-button icon-button-small btn-danger ml-2"
              >
                <span className="material-icons">link_off</span>
              </button>
            ) : record.fundsLedgerId ? (
              isAccountingLedger ? (
                <button
                  onClick={onUnlinkLedger}
                  className="d-block base-button icon-button icon-button-small btn-danger ml-2"
                  title="Unlink from Ledger"
                >
                  <span className="material-icons">link_off</span>
                </button>
              ) : null
            ) : (
              <button
                className="base-button icon-button icon-button-small btn-danger ml-2"
                onClick={onDelete}
                title="Delete"
              >
                <span className="material-icons">delete</span>
              </button>
            )}
              </>
            )}
          </div>
        </td>
      </tr>
      <tr className={`payment-records-table-row-expandable ${isOpen ? 'payment-records-table-row-expanded' : ''}`}>
        <td colSpan={columns.length + 2}>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            {record.notes || "-"}
          </Collapse>
        </td>
      </tr>
    </>
  );
};

const PaymentModule = (props) => {
  const { getAccessTokenSilently } = useAuth0();

  //filter deleted records
  let records = props.records?.filter((record) => !record.deleted);

  let initialState = {
    saving: false,
    showFormModal: false,
    showDeleteModal: false,
    recordBeingEdited: null,
    paymentDate: "",
    paymentType: "",
    transactionType: {
      label: "Received",
      value: "received",
    },
    amount: "",
    records: records,
    loading: false,
    selectedContract: null,
    selectedOFR: null,
    operatorId: "",
    operatorName: "",
    datePickerLabel: "Payment Date",
    notes: "",
    showNotes: false,
    showContractSelect: false,
    showOFRSelect: false,
    contracts: props.contracts || [],
    operatorFinancialRecords: props.operatorFinancialRecords || [],
    showUnlinkModalOpen: false,
    externalPayment: {},
    linkModalValues: {},
  };

  if (props.recordType === "clientPaymentSchedule") {
    initialState.recordTypeLabel = "Client Payment Schedule";
    initialState.datePickerLabel = "Due Date";
    initialState.title = "Client Payment";
  }

  if (props.recordType === "clientPaymentHistory") {
    initialState.recordTypeLabel = "Client Payment History";
    initialState.showContractSelect = true;
    initialState.showNotes = true;
    initialState.title = "Client Payment";
  }

  if (props.recordType === "operatorPaymentHistory") {
    initialState.recordTypeLabel = "Operator Payment History";
    initialState.showOFRSelect = true;
    initialState.showNotes = true;
    initialState.title = "Operator Payment";
  }

  const [state, dispatch] = useReducer(reducer, initialState);

  const { setUnsavedChanges } = props;

  useEffect(() => {
    setUnsavedChanges?.(
      {
        section: "paymentModal",
        value: !!state.showFormModal,
      },
    );
  }, [setUnsavedChanges, state.showFormModal]);

  //create function to handle edit button, show react Modal with input field
  const handleEditButton = (item) => {
    dispatch({ type: "resetForm" });

    //set recordBeingEdited
    dispatch({
      type: "fieldChanged",
      field: "recordBeingEdited",
      value: item,
    });

    //show modal
    dispatch({
      type: "editRecordForm",
      //pass record to be edited
      record: item,
    });
  };

  const handleAddRecord = async (e) => {
    e.preventDefault();

    const formData = {
      paymentType: state.paymentType?.value,
      amount: Math.abs(state.amount),
      paymentDate: state.paymentDate,
    };

    if (state.transactionType?.value === "spent") {
      formData.amount = formData.amount * -1;
    }

    if (state.showNotes) {
      formData.notes = state.notes;
    }

    if (state.showOFRSelect) {
      formData.ofrId = state.selectedOFR?.value;
      formData.operator = {
        id: state.selectedOFR?.operator?.id,
        name: state.selectedOFR?.operator?.name,
      };
    }
    if (state.showContractSelect) {
      formData.contractId = state.selectedContract?.value;
    }

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: true,
    });

    const token = await getAccessTokenSilently();

    try {
      await ai.auth(token).post(
        `/api/trips/${props.tripId}/addPaymentRecord/${props.recordType}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
    } catch (error) {
      dispatch({ type: "fieldChanged", field: "saving", value: false });
      return toast.error(
        getApiErrorMessage(error, "Couldn't save the payment."),
        toastOptions
      );
    }

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: false,
    });

    //reset form
    dispatch({ type: "resetForm" });

    props.parentDispatch({
      type: "fieldChanged",
      field: "refreshDataToken",
      value: Date.now(),
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!state.paymentDate || !state.paymentType || !state.transactionType || (!state.amount && state.amount !== 0) || (state.showContractSelect && !state.selectedContract) || (state.showOFRSelect && !state.selectedOFR)) {
      return toast.error("Please fill in all required fields and retry.", toastOptions);
    }

    if (((state.showContractSelect && !state.selectedContract?.value) || (state.showOFRSelect && !state.selectedOFR?.value)) && !state.notes) {
      return toast.error("Please fill in all required fields and retry.", toastOptions);
    }

    if (state.amount === 0) {
      return toast.error("Amount can't be $0", toastOptions);
    }

    //if recordBeingEdited is null, add record
    if (!state.recordBeingEdited) {
      handleAddRecord(e);
    }

    //if recordBeingEdited is not null, edit record
    if (state.recordBeingEdited) {
      handleEditSubmit(e);
    }
  };

  //create function to edit a note
  const handleEditSubmit = async (e) => {
    e.preventDefault();

    const formData = {
      paymentType: state.paymentType?.value,
      amount: Math.abs(state.amount),
      paymentDate: state.paymentDate,
    };

    if (state.transactionType?.value === "spent") {
      formData.amount = formData.amount * -1;
    }

    if (state.showNotes) {
      formData.notes = state.notes;
    }

    if (state.showOFRSelect) {
      formData.ofrId = state.selectedOFR?.value;
      formData.operator = {
        id: state.selectedOFR?.operator?.id,
        name: state.selectedOFR?.operator?.name,
      };
    }
    if (state.showContractSelect) {
      formData.contractId = state.selectedContract?.value;
    }

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: true,
    });

    const token = await getAccessTokenSilently();

    try {
      await ai.auth(token).put(
        `/api/trips/${props.tripId}/updatePaymentRecord/${props.recordType}/${state.recordBeingEdited._id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
    } catch (error) {
      dispatch({ type: "fieldChanged", field: "saving", value: false });
      return toast.error(
        getApiErrorMessage(error, "Couldn't update the payment."),
        toastOptions
      );
    }

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: false,
    });

    dispatch({ type: "fieldChanged", field: "showFormModal", value: false });

    dispatch({ type: "resetForm" });

    props.parentDispatch({
      type: "fieldChanged",
      field: "refreshDataToken",
      value: Date.now(),
    });
  };

  //function to delete a note
  const handleDeleteButton = async (record) => {
    dispatch({
      type: "fieldChanged",
      field: "showDeleteModal",
      value: true,
    });
    dispatch({
      type: "fieldChanged",
      field: "recordBeingEdited",
      value: record,
    });
  };

  //function to delete a note
  const handleDeleteConfirm = async () => {
    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: true,
    });

    const token = await getAccessTokenSilently();

    try {
      await ai.auth(token).delete(
        `/api/trips/${props.tripId}/deletePaymentRecords/${props.recordType}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          data: {
            recordIds: [state.recordBeingEdited._id],
          },
        },
      );
    } catch (error) {
      dispatch({ type: "fieldChanged", field: "saving", value: false });
      return toast.error(
        getApiErrorMessage(error, "Couldn't delete the payment."),
        toastOptions
      );
    }

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: false,
    });

    dispatch({ type: "resetForm" });

    props.parentDispatch({
      type: "fieldChanged",
      field: "refreshDataToken",
      value: Date.now(),
    });
  };

  const handlePriceFieldChange = (values, sourceInfo) => {
    const { value } = values;
    // Event is a Synthetic Event wrapper which holds target and other information. Source tells whether the reason for this function being triggered was an 'event' or due to a 'prop' change
    const { event } = sourceInfo;

    if (!event?.target) {
      return;
    }

    dispatch({
      type: "fieldChanged",
      field: event?.target?.name,
      value: value,
      fieldType: "float",
    });
  };

  const netPayments = useMemo(
    () => (state.records || [])
      .filter(record => record.paymentType !== "ccAuthorization")
      .reduce((sum, { amount }) => sum + +amount, 0),
    [state.records],
  );

  const contractOptions = useMemo(
    () => ([
      {
        value: null,
        label: "N/A",
      },
      ...(props.contracts || []).map((contract) => ({
        value: contract._id,
        label: formatContractOptionLabel(contract),
      })),
    ]),
    [props.contracts],
  );
  const ofrOptions = useMemo(
    () => ([
      {
        value: null,
        label: "N/A",
      },
      ...(props.operatorFinancialRecords || []).map((ofr) => ({
        value: ofr._id,
        label: formatOFROptionLabel(ofr),
        operator: ofr.operator,
      })),
    ]),
    [props.operatorFinancialRecords],
  );

  const handleCloseModal = () => {
    dispatch({
      type: "fieldChanged",
      field: "showFormModal",
      value: false,
    });
    dispatch({
      type: "fieldChanged",
      field: "showDeleteModal",
      value: false,
    });
  };

  const handleUnlinkExternalPayment = (record, targetingPayments) => {
    dispatch({
      type: "fieldChanged",
      field: "showUnlinkModalOpen",
      value: true,
    });
    dispatch({
      type: "fieldChanged",
      field: "externalPayment",
      value: {
        _id: record.plaidTransactionId || record.xeroTransactionId,
        _lineItemID: record.lineItemId || null,
        targetingPayments,
      },
    });
  };

  const handleCloseLinkProcessingModal = () => {
    dispatch({
      type: "fieldChanged",
      field: "showUnlinkModalOpen",
      value: false,
    });
    dispatch({
      type: "fieldChanged",
      field: "externalPayment",
      value: {},
    });
    dispatch({
      type: "fieldChanged",
      field: "linkModalValues",
      value: {},
    });

    props.parentDispatch({
      type: "fieldChanged",
      field: "refreshDataToken",
      value: Date.now(),
    });
  };

  return (
    <>
      <div
        className="trip-notes-module"
        id={`trip-${props.recordType}`}
        key={`key-catering-${props.dataDownloadedTimestamp.toString()}`}
      >
        <h5 className="mb-1 d-flex">
          {props.title}

          {!props.readOnly && (
            <button
              className="base-button icon-button icon-button-small evo-dark-green ml-2"
              onClick={() => {
                dispatch({ type: "resetForm" });
                dispatch({
                  type: "addRecordForm",
                });
              }}
              title="Add"
            >
              <span className="material-icons">add_circle</span>
            </button>
          )}
        </h5>

        <div className="payment-records-table" id={`trip-${props.recordType}`}>
          <table>
            <thead className="fm-l2-parent-table-head dynamic-accordion-table-header">
            <tr className="fm-l2-parent-table-head-row">
              <th />
              {props.columns.map((column) => (
                <th key={`column-${column.label}`}>{column.label}</th>),
              )}
              <th className="text-align-right">Actions</th>
            </tr>
            </thead>
            <tbody className="fm-l2-parent-table-body">
            {state.records?.length > 0 &&
              state.records.map((record) => (
                <PaymentModuleRow
                  record={record}
                  columns={props.columns}
                  contracts={props.contracts}
                  operatorFinancialRecords={props.operatorFinancialRecords}
                  handleUnlinkExternalPayment={handleUnlinkExternalPayment}
                  handleEditButton={handleEditButton}
                  handleDeleteButton={handleDeleteButton}
                  onUnlinkLedgerPayment={props.onUnlinkLedgerPayment}
                  onRevertFundsMovement={props.onRevertFundsMovement}
                  isAccountingLedger={props.isAccountingLedger}
                  readOnly={props.readOnly}
                />
              ))}

            {state.records?.length === 0 && (
              <tr className="fm-l2-parent-table-row">
                <td colSpan={props.columns.length + 2}>
                  {props.recordType === "operatorPaymentHistory" ? "Operator" : "Client"} payments have not been added
                  yet.
                </td>
              </tr>
            )}

            {state.records?.length > 0 && (
              <tr className="fm-l2-parent-table-row">
                <td className="text-align-right" colSpan={props.columns.length}>
                  Net Payments
                </td>
                <td className={`text-align-right amount-${netPayments >= 0 ? "positive" : "negative"}`}>
                  {netPayments.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
                </td>
                <td/>
              </tr>
            )}
            </tbody>
          </table>
        </div>
      </div>

      <ReactModal
        isOpen={state.showFormModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            border: "none",
            maxWidth: "700px",
            maxHeight: "80vh",
            minWidth: "50vw",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header">
          <h4>{state.modalHeaderText} {state.title || "Record"}</h4>
        </div>
        <div className="modal-body width-100">
          <form
            onSubmit={handleFormSubmit}
            className="width-100 base-form mt-20"
          >
            {state.showContractSelect && (
              <div className="form-group">
                <label htmlFor={`selectedContract`} className="base-label">
                  Contract <span className="required">*</span>
                </label>

                <Select
                  className="selectedContract-select"
                  classNamePrefix="select"
                  options={contractOptions}
                  placeholder="Contract"
                  onChange={(selectedValue) => {
                    dispatch({
                      type: "fieldChanged",
                      field: "selectedContract",
                      value: selectedValue,
                    });
                  }}
                  key={`key-selected-contract`}
                  value={state.selectedContract}
                  name="selectedContract"
                  required
                />
              </div>
            )}

            {state.showOFRSelect && (
              <div className="form-group">
                <label htmlFor={`selectedOFR`} className="base-label">
                  OFR <span className="required">*</span>
                </label>

                <Select
                  className="selectedOFR-select"
                  classNamePrefix="select"
                  options={ofrOptions}
                  placeholder="OFR"
                  onChange={(selectedValue) => {
                    dispatch({
                      type: "fieldChanged",
                      field: "selectedOFR",
                      value: selectedValue,
                    });
                  }}
                  key={`key-selected-ofr`}
                  value={state.selectedOFR}
                  name="selectedOFR"
                  required
                />
              </div>
            )}

            <div className="w-100 d-flex gap-20">
              <div className="form-group flex-half">
                <label htmlFor={`paymentType`} className="base-label">
                  Payment Type <span className="required">*</span>
                </label>

                <Select
                  className="paymentType-select"
                  classNamePrefix="select"
                  options={state.showOFRSelect ? OPERATOR_PAYMENT_TYPE_OPTIONS : CLIENT_PAYMENT_TYPE_OPTIONS}
                  placeholder="Payment Type"
                  onChange={(selectedValue) => {
                    dispatch({
                      type: "fieldChanged",
                      field: "paymentType",
                      value: selectedValue,
                    });
                  }}
                  key={`key-payment-type`}
                  value={state.paymentType}
                  name="paymentType"
                  required
                />
              </div>

              <div className="datepickerContainer mb-30 flex-half">
                <label htmlFor={`date`} className="base-label">
                  Date <span className="required">*</span>
                </label>

                <Datepicker
                  value={state.paymentDate}
                  onChange={(event) => {
                    dispatch({
                      type: "fieldChanged",
                      field: "paymentDate",
                      value: event.value,
                    });
                  }}
                  dataTimezone="utc"
                  displayTimezone="local"
                  timezonePlugin={momentTimezone}
                  controls={["date"]}
                />
              </div>
            </div>

            <div className="w-100 d-flex gap-20">
              <div className="form-group flex-half">
                <label htmlFor={`transactionType`} className="base-label">
                  Transaction Type <span className="required">*</span>
                </label>

                <Select
                  className="transactionType-select"
                  classNamePrefix="select"
                  options={[
                    {
                      label: "Received",
                      value: "received",
                    },
                    {
                      label: "Spent",
                      value: "spent",
                    },
                  ]}
                  placeholder="Transaction Type"
                  onChange={(selectedValue) => {
                    dispatch({
                      type: "fieldChanged",
                      field: "transactionType",
                      value: selectedValue,
                    });
                  }}
                  key={`key-transaction-type`}
                  value={state.transactionType}
                  name="transactionType"
                  required
                />
              </div>

              <div className="form-group flex-half">
                <label htmlFor={`amount`} className="base-label">
                  Amount <span className="required">*</span>
                </label>

                <NumberFormat
                  value={state.amount}
                  thousandSeparator={true}
                  prefix={"$"}
                  onValueChange={handlePriceFieldChange}
                  placeholder="Amount"
                  className={`semibold-text amount-${state.amount >= 0 ? "positive" : "negative"}`}
                  decimalScale={2}
                  name="amount"
                  allowNegative={state.transactionType?.value === "spent"}
                />
              </div>
            </div>

            {state.showNotes && (
              <TextAreaInput
                label="Notes"
                name="notes"
                value={state.notes}
                handleChange={(e) => {
                  dispatch({
                    type: "fieldChanged",
                    field: "notes",
                    value: e.target.value,
                  });
                }}
                required={(state.showContractSelect && !state.selectedContract?.value) || (state.showOFRSelect && !state.selectedOFR?.value)}
              />
            )}

            <LoadingButton className="base-button evo-blue w-100" type="submit" isLoading={state.saving}>
              Submit
            </LoadingButton>

            <button
              className="base-button btn-danger w-100 mt-10"
              type="button"
              onClick={handleCloseModal}
            >
              Cancel
            </button>
          </form>
        </div>
      </ReactModal>

      <ReactModal
        isOpen={state.showDeleteModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        className="trip-notes-module-modal"
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "50%",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>Delete {state.title || "Record"}</h4>
        </div>

        <div className="modal-body base-modal overflow-scroll">
          <div className="w-100 mt-20">
            This {state.title?.toLowerCase() || ""} record will be permanently deleted from the trip. Are you sure you
            want to continue?
          </div>

          <div className="w-100 mt-20 d-flex gap-20">
            <button className="base-button evo-blue flex-half" type="button" onClick={handleCloseModal}
                    disabled={state.saving}>
              Cancel
            </button>

            <LoadingButton className="base-button btn-danger flex-half" type="button" onClick={handleDeleteConfirm}
                           isLoading={state.saving}>
              Delete
            </LoadingButton>
          </div>
        </div>
      </ReactModal>

      <PaymentUnlinkModal
        payment={state.externalPayment}
        open={state.showUnlinkModalOpen}
        onRequestClose={handleCloseLinkProcessingModal}
      />
    </>
  );
};

export default PaymentModule;
